import styled from 'styled-components';

import { mapQuery } from 'utils/query';
import { List } from 'components/list';
import { formatPrice } from 'utils/helpers';
import { ListCard } from 'components/list-card';
import { ListHeader } from 'components/list-header';
import { useEventMachinesQuery } from 'store/api/clover-device';
import { formatMachineDays, formatMachineName } from 'utils/card-machines';

// CARD MACHINES (owner 2 Nov 2026: "add to the event profile what machines
// we are using... we always [want] a log of what machine we used for this
// event"). One row per machine that took payments here (its days, payments
// and card sales), then the machines assigned here now with no payment yet.
// Card sales stay hidden where the profile hides the event's money.
// On iPad a machine stays one row (the list's default 3 columns pushed Card
// sales onto a second line); measured at 820 with the longest Prod name.
// On a phone the name and the days get the full width (two columns cut
// both with "…" at 390), then payments and card sales side by side.
const cellsMobile = [
  { row: '1', col: '1 / 3' },
  { row: '2', col: '1 / 3' },
  { row: '3', col: '1' },
  { row: '3', col: '2' },
];

// Nothing yet: one plain line under the title, like the profile's other
// empty values (not the list's large illustration).
// Ends 40 px above the card's edge, as Team and Notes do (measured).
const EmptyLine = styled.p`
  margin: 0 0 24px;
`;

export const NO_MACHINES_TEXT = 'No card machines used at this event yet';
export const ASSIGNED_UNUSED_TEXT = 'Assigned, no payments yet';

export const EventCardMachines = ({
  eventId,
  hideMoney,
}: {
  eventId: number;
  hideMoney: boolean;
}) => {
  const { data, ...props } = useEventMachinesQuery(eventId);

  const titles = ['Machine', 'Days', 'Payments'];
  if (!hideMoney) {
    titles.push('Card sales');
  }

  const tabletTemplate = hideMoney
    ? '1.6fr 1.6fr 0.7fr'
    : '1.6fr 1.6fr 0.7fr 0.9fr';

  const used = data?.used ?? [];
  const assignedUnused = data?.assignedUnused ?? [];
  const count = used.length + assignedUnused.length;
  const blank = hideMoney ? [] : [''];

  return (
    <List
      {...mapQuery(props)}
      count={count}
      testId="event-card-machines"
      // No header over the empty line (the list shows it on every state).
      header={count ? <ListHeader titles={titles} /> : undefined}
      dataPlaceholder={<EmptyLine>{NO_MACHINES_TEXT}</EmptyLine>}
    >
      {used.map((row) => (
        <ListCard
          headers={titles}
          cellsMobile={cellsMobile}
          key={`used-${row.deviceId}`}
          tabletTemplate={tabletTemplate}
          values={[
            formatMachineName(row),
            formatMachineDays(row.firstDay, row.lastDay),
            row.payments,
            ...(hideMoney ? [] : [formatPrice((row.amount ?? 0) / 100)]),
          ]}
        />
      ))}
      {assignedUnused.map((row) => (
        <ListCard
          headers={titles}
          cellsMobile={cellsMobile}
          tabletTemplate={tabletTemplate}
          key={`assigned-${row.deviceId}`}
          values={[formatMachineName(row), ASSIGNED_UNUSED_TEXT, '', ...blank]}
        />
      ))}
    </List>
  );
};
